// Model prices (was "Price book", SCR-005) view model: the book's rows with the day each stops
// applying, the filter bar (applied to the whole book, which the API answers in one page), and the
// Add-price form (API-018).
// Reading is open to billing principals and super-admins; adding and deleting a row is
// super-admin only (enforced by the server; the screen just hides the controls, ADR-031).
import type { components } from "../../api/schema";
import type { ApiError } from "@nexus/ui";
import { fieldErrorsFrom, required, usdAmount, validate, type FieldErrors, type Rule } from "@nexus/ui";
import { parseUsd } from "@nexus/ui";

export type ModelPrice = components["schemas"]["ModelPrice"];
export type CreatePriceRequest = components["schemas"]["CreatePriceRequest"];

export interface PriceFilters {
  /** Part of a model id (case-insensitive). */
  model: string;
  /** `YYYY-MM-DD` (UTC): per model, only the row in force that day. */
  effectiveOn: string;
}

export const EMPTY_FILTERS: PriceFilters = { model: "", effectiveOn: "" };

export interface PriceRowView {
  row: ModelPrice;
  /** Wireframe: unverified rows are marked with the word "Unverified", never colour alone. */
  unverified: boolean;
  sourceHref: string | null;
  /** The last day this row applies (the day before the model's next row), or null while current. */
  validTo: string | null;
}

export function priceRowView(row: ModelPrice, validTo: string | null = null): PriceRowView {
  return { row, unverified: !row.verified, sourceHref: row.sourceUrl ?? null, validTo };
}

/** The UTC day before a `YYYY-MM-DD` date. */
export function dayBefore(date: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - 1);
  return day.toISOString().slice(0, 10);
}

/**
 * Row views for the whole book (the API answers it whole, ordered by model then validFrom), each
 * with the day it stops applying: the day before the same model's next row.
 */
export function priceRows(prices: readonly ModelPrice[]): PriceRowView[] {
  const byModel = new Map<string, ModelPrice[]>();
  for (const price of prices) {
    const list = byModel.get(price.bedrockModelId) ?? [];
    list.push(price);
    byModel.set(price.bedrockModelId, list);
  }
  const next = new Map<ModelPrice, string>();
  for (const list of byModel.values()) {
    const ordered = [...list].sort((a, b) => a.validFrom.localeCompare(b.validFrom));
    ordered.forEach((price, i) => {
      const following = ordered[i + 1];
      if (following) next.set(price, dayBefore(following.validFrom));
    });
  }
  return prices.map((price) => priceRowView(price, next.get(price) ?? null));
}

/** Whether a row applies on a day: from its validFrom through its validTo (open-ended while current). */
export function appliesOn(view: PriceRowView, day: string): boolean {
  return view.row.validFrom <= day && (view.validTo === null || view.validTo >= day);
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The filter bar, applied to the whole book: a model-id search (part of the id, any case) and
 * "valid on" a day — the same per-model rule as the API's `effectiveOn`. An incomplete date is
 * ignored rather than hiding everything.
 */
export function filterPriceRows(rows: readonly PriceRowView[], filters: PriceFilters): PriceRowView[] {
  const search = filters.model.trim().toLowerCase();
  const day = filters.effectiveOn.trim();
  return rows.filter(
    (view) => (search === "" || view.row.bedrockModelId.toLowerCase().includes(search)) && (!DAY.test(day) || appliesOn(view, day)),
  );
}

/** How many different models the rows cover. */
export function modelCount(rows: readonly PriceRowView[]): number {
  return new Set(rows.map((view) => view.row.bedrockModelId)).size;
}

// ---- Add price dialog (super-admin, API-018) ----

export interface AddPriceFormValues {
  bedrockModelId: string;
  /** "" means tomorrow, UTC (the server's default). */
  validFrom: string;
  inputPerM: string;
  outputPerM: string;
  cacheReadPerM: string;
  cacheWritePerM: string;
  /** "" means priced at `cacheWritePerM` (ADR-018). */
  cacheWrite1hPerM: string;
  source: string;
  sourceUrl: string;
  retrievedOn: string;
  verified: boolean;
}

export const EMPTY_ADD_PRICE_FORM: AddPriceFormValues = {
  bedrockModelId: "",
  validFrom: "",
  inputPerM: "",
  outputPerM: "",
  cacheReadPerM: "",
  cacheWritePerM: "",
  cacheWrite1hPerM: "",
  source: "",
  sourceUrl: "",
  retrievedOn: "",
  verified: false,
};

const dateOrEmpty: Rule = (value) => (value.trim() === "" || /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? null : "A date as YYYY-MM-DD.");
const optionalUsdAmount: Rule = (value) => (value.trim() === "" ? null : usdAmount(value));

/**
 * Client-side checks mirroring API-018 (the server re-validates): required fields, the five
 * rates as dollar amounts, dates, and `verified: true` requiring `sourceUrl` and `retrievedOn`
 * (ADR-025).
 */
export function validateAddPriceForm(values: AddPriceFormValues): FieldErrors {
  const errors = validate(
    {
      bedrockModelId: values.bedrockModelId,
      validFrom: values.validFrom,
      inputPerM: values.inputPerM,
      outputPerM: values.outputPerM,
      cacheReadPerM: values.cacheReadPerM,
      cacheWritePerM: values.cacheWritePerM,
      cacheWrite1hPerM: values.cacheWrite1hPerM,
      source: values.source,
      retrievedOn: values.retrievedOn,
    },
    {
      bedrockModelId: [required],
      validFrom: [dateOrEmpty],
      inputPerM: [required, usdAmount],
      outputPerM: [required, usdAmount],
      cacheReadPerM: [required, usdAmount],
      cacheWritePerM: [required, usdAmount],
      cacheWrite1hPerM: [optionalUsdAmount],
      source: [required],
      retrievedOn: [dateOrEmpty],
    },
  );
  if (values.verified) {
    if (values.sourceUrl.trim() === "") errors.sourceUrl ??= "Required when verified.";
    if (values.retrievedOn.trim() === "") errors.retrievedOn ??= "Required when verified.";
  }
  return errors;
}

/** Whether `validFrom` needs `?backdate=true` (today or earlier, UTC) — API-018/ADR-017. */
export function needsBackdate(validFrom: string, today: Date): boolean {
  if (validFrom.trim() === "") return false;
  return validFrom.trim() <= today.toISOString().slice(0, 10);
}

export interface AddPriceRequest {
  body: CreatePriceRequest;
  backdate: boolean;
}

/** Builds the API request from validated values; call only once `validateAddPriceForm` is empty. */
export function addPriceRequestFrom(values: AddPriceFormValues, today: Date): AddPriceRequest {
  const micro = (value: string): number => Number(parseUsd(value));
  const validFrom = values.validFrom.trim();
  const cacheWrite1h = values.cacheWrite1hPerM.trim();
  const sourceUrl = values.sourceUrl.trim();
  const retrievedOn = values.retrievedOn.trim();
  const body: CreatePriceRequest = {
    bedrockModelId: values.bedrockModelId.trim(),
    ...(validFrom !== "" ? { validFrom } : {}),
    inputPerM: micro(values.inputPerM),
    outputPerM: micro(values.outputPerM),
    cacheReadPerM: micro(values.cacheReadPerM),
    cacheWritePerM: micro(values.cacheWritePerM),
    ...(cacheWrite1h !== "" ? { cacheWrite1hPerM: micro(cacheWrite1h) } : {}),
    source: values.source.trim(),
    ...(sourceUrl !== "" ? { sourceUrl } : {}),
    ...(retrievedOn !== "" ? { retrievedOn } : {}),
    verified: values.verified,
  };
  return { body, backdate: needsBackdate(validFrom, today) };
}

/** Field errors from a 422/409 the server answered on submit (e.g. `price_exists`). */
export function priceFormServerErrors(error: ApiError): FieldErrors {
  return fieldErrorsFrom(error);
}
